import { useEffect, useState } from "react";
import { Lock, Menu, Plus, X } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePlan, usePlanLimit } from "../context/PlanContext";
import { MyPageMenu } from "./MyPageMenu";
import { PlanLimitBanner } from "./PlanLimitBanner";
import { PlanLockedModule } from "./PlanLockedModule";
import { SidebarNav } from "./SidebarNav";
import { MODULE_GATES, modules, pageEyebrow, pageTitle, settingsNavigation } from "./navigation";
import { useSeo } from "../lib/seo";
import { BrandLogo } from "./BrandLogo";

const LIMIT_BANNERS = {
  "/utensili": "tools", "/attacchi-base": "base_attachments", "/magazzini": "warehouses",
  "/componenti": "parts", "/pezzi": "parts", "/macchine": "machines", "/produzione/ordini": "jobs", "/utenti": "users",
};

export default function AppShell({ children }) {
  const location = useLocation();
  const { session, logout } = useAuth();
  const { plan } = usePlan();
  const toolsLimit = usePlanLimit("tools");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const visibleModules = modules
    .map((module) => ({
      ...module,
      items: module.items.filter((item) => session.user.permissions.includes(item.permission)
        && (!item.anyPermission || item.anyPermission.some((permission) => session.user.permissions.includes(permission)))),
    }))
    .filter((module) => module.items.length > 0);
  const lockedGates = plan ? MODULE_GATES.filter((gate) => !plan.modules.includes(gate.module)) : [];
  const lockedModules = lockedGates.map((gate) => gate.module);
  const openPrefixes = MODULE_GATES
    .filter((gate) => !lockedModules.includes(gate.module))
    .flatMap((gate) => gate.prefixes);
  const lockedGate = lockedGates.find((gate) => gate.prefixes.some((prefix) => location.pathname.startsWith(prefix)
    && !openPrefixes.some((open) => open.length > prefix.length && location.pathname.startsWith(open))));
  const lockedModuleName = lockedGate ? lockedGate.label : "";
  const bannerResource = LIMIT_BANNERS[location.pathname];

  useSeo({
    title: `${pageTitle(location.pathname)} | ToolsValue Officina Tool Control`,
    description: "Area aziendale riservata ToolsValue: gestione utensili, magazzini, produzione e qualità dell'officina.",
    path: location.pathname,
    noindex: true,
  });

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    // col menu del telefono aperto la pagina sotto non deve scorrere
    document.body.classList.toggle("mobile-menu-open", isMenuOpen);
    return () => document.body.classList.remove("mobile-menu-open");
  }, [isMenuOpen]);

  return (
    <div className="app-shell" data-testid="application-shell">
      <aside className={`sidebar${isMenuOpen ? " is-open" : ""}`} data-testid="main-sidebar">
        <div className="brand" data-testid="brand-identity">
          <BrandLogo size={52} className="brand-logo-shell" />
          <span><b>OFFICINA</b><small>TOOL CONTROL</small></span>
        </div>
        <SidebarNav modules={visibleModules} lockedModules={lockedModules} mobileOpen={isMenuOpen} onNavigate={() => setIsMenuOpen(false)} />
        <MyPageMenu session={session} logout={logout} settingsNavigation={settingsNavigation} />
      </aside>
      {isMenuOpen && (
        <button type="button" className="nav-backdrop" aria-label="Chiudi il menu" onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-backdrop" />
      )}
      <main className="main-panel">
        <header className="topbar" data-testid="page-header">
          <div className="topbar-lead">
            <button
              type="button"
              className="mobile-nav-toggle"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? "Chiudi il menu" : "Apri il menu"}
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
              <span>Menu</span>
            </button>
            <div>
              <p className="eyebrow">{pageEyebrow(location.pathname)}</p>
              <h1 data-testid="page-title">{pageTitle(location.pathname)}</h1>
            </div>
          </div>
          {location.pathname.startsWith("/utensili") && (toolsLimit.reached ? (
            <button type="button" className="primary-button" disabled title={toolsLimit.hint} data-testid="new-tool-button">
              <Lock size={16} /> Nuovo utensile
            </button>
          ) : (
            <NavLink to="/utensili?new=1" className="primary-button" data-testid="new-tool-button"><Plus size={18} weight="bold" /> Nuovo utensile</NavLink>
          ))}
        </header>
        {bannerResource && <PlanLimitBanner resource={bannerResource} />}
        {lockedModuleName ? <PlanLockedModule module={lockedModuleName} /> : children}
      </main>
    </div>
  );
}
